import React, { useState, useEffect } from 'react';
import {
  fetchTutorials,
  fetchTutorialById,
  createTutorial,
  updateTutorial,
  toggleTutorialPublish,
  deleteTutorial,
} from '../../api/adminClient';

const TutorialManagement = () => {
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [viewTutorial, setViewTutorial] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    file: null,
  });

  useEffect(() => {
    loadTutorials();
  }, [statusFilter]);

  const loadTutorials = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter === 'published') {
        params.status = 'published';
      } else if (statusFilter === 'draft') {
        params.status = 'draft';
      }
      
      const response = await fetchTutorials(params);
      if (response.data?.success) {
        setTutorials(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load tutorials:', error);
      alert('Failed to load tutorials');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setSelectedTutorial(null);
    setFormData({ title: '', content: '', file: null });
    setShowModal(true);
  };

  const handleOpenEditModal = (tutorial) => {
    setModalMode('edit');
    setSelectedTutorial(tutorial);
    setFormData({
      title: tutorial.title || '',
      content: tutorial.content || '',
      file: null,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTutorial(null);
    setFormData({ title: '', content: '', file: null });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (500MB max)
      if (file.size > 500 * 1024 * 1024) {
        alert('File size must be less than 500MB');
        e.target.value = '';
        return;
      }
      
      // Validate file type
      const allowedTypes = [
        'video/mp4',
        'video/mpeg',
        'video/quicktime',
        'video/x-msvideo',
        'video/x-ms-wmv',
        'video/webm',
        'application/pdf',
      ];
      
      if (!allowedTypes.includes(file.type)) {
        alert('Invalid file type. Allowed: MP4, MPEG, MOV, AVI, WMV, WEBM, PDF');
        e.target.value = '';
        return;
      }
      
      setFormData((prev) => ({ ...prev, file }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      alert('Title is required');
      return;
    }

    setLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      if (formData.content) {
        formDataToSend.append('content', formData.content);
      }
      if (formData.file) {
        formDataToSend.append('file', formData.file);
      }

      if (modalMode === 'create') {
        await createTutorial(formDataToSend);
        alert('Tutorial created successfully');
      } else {
        await updateTutorial(selectedTutorial.uuid, formDataToSend);
        alert('Tutorial updated successfully');
      }

      handleCloseModal();
      loadTutorials();
    } catch (error) {
      console.error('Failed to save tutorial:', error);
      alert(error.response?.data?.error || 'Failed to save tutorial');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublish = async (tutorial) => {
    const newStatus = !tutorial.isPublished;
    const confirmMsg = newStatus
      ? 'Are you sure you want to publish this tutorial?'
      : 'Are you sure you want to unpublish this tutorial?';
    
    if (!window.confirm(confirmMsg)) {
      return;
    }

    setLoading(true);
    try {
      await toggleTutorialPublish(tutorial.uuid, newStatus);
      alert(`Tutorial ${newStatus ? 'published' : 'unpublished'} successfully`);
      loadTutorials();
    } catch (error) {
      console.error('Failed to toggle publish status:', error);
      alert(error.response?.data?.error || 'Failed to update tutorial status');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (tutorial) => {
    if (!window.confirm('Are you sure you want to delete this tutorial? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      await deleteTutorial(tutorial.uuid);
      alert('Tutorial deleted successfully');
      loadTutorials();
    } catch (error) {
      console.error('Failed to delete tutorial:', error);
      alert(error.response?.data?.error || 'Failed to delete tutorial');
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (tutorial) => {
    setLoading(true);
    try {
      const response = await fetchTutorialById(tutorial.uuid);
      if (response.data?.success) {
        setViewTutorial(response.data.data);
        setShowViewModal(true);
      }
    } catch (error) {
      console.error('Failed to load tutorial details:', error);
      alert('Failed to load tutorial details');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewTutorial(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="tutorial-management">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 fw-bold mb-1">How-to / Tutorials</h1>
          <p className="text-muted mb-0">Manage instructional videos and PDFs for users</p>
        </div>
        <button
          className="btn btn-primary rounded-pill px-4"
          onClick={handleOpenCreateModal}
          disabled={loading}
        >
          <i className="bi bi-plus-circle me-2"></i>
          Create Tutorial
        </button>
      </div>

      <div className="card-soft mb-4">
        <div className="d-flex gap-2 align-items-center">
          <span className="text-muted fw-medium">Filter:</span>
          <div className="btn-group" role="group">
            <button
              type="button"
              className={`btn btn-sm ${statusFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setStatusFilter('all')}
            >
              All
            </button>
            <button
              type="button"
              className={`btn btn-sm ${statusFilter === 'published' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setStatusFilter('published')}
            >
              Published
            </button>
            <button
              type="button"
              className={`btn btn-sm ${statusFilter === 'draft' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setStatusFilter('draft')}
            >
              Drafts
            </button>
          </div>
        </div>
      </div>

      {loading && tutorials.length === 0 ? (
        <div className="card-soft text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted mt-3 mb-0">Loading tutorials...</p>
        </div>
      ) : tutorials.length === 0 ? (
        <div className="card-soft text-center py-5">
          <i className="bi bi-collection-play display-1 text-muted mb-3"></i>
          <h5 className="text-muted">No tutorials found</h5>
          <p className="text-muted mb-0">Create your first tutorial to get started</p>
        </div>
      ) : (
        <div className="row g-4">
          {tutorials.map((tutorial) => (
            <div key={tutorial.uuid} className="col-12 col-md-6 col-xl-4">
              <div className="card-soft h-100 d-flex flex-column">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div className="flex-grow-1">
                    <h5 className="fw-semibold mb-2">{tutorial.title}</h5>
                    <span
                      className={`badge ${
                        tutorial.isPublished
                          ? 'bg-success-subtle text-success'
                          : 'bg-warning-subtle text-warning'
                      } rounded-pill`}
                    >
                      {tutorial.isPublished ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  {tutorial.azurebloblink && (
                    <i className="bi bi-file-earmark-play text-primary fs-4"></i>
                  )}
                </div>

                {tutorial.content && (
                  <p className="text-muted small mb-3 flex-grow-1" style={{ maxHeight: '60px', overflow: 'hidden' }}>
                    {tutorial.content.substring(0, 120)}
                    {tutorial.content.length > 120 ? '...' : ''}
                  </p>
                )}

                <div className="mb-3 small text-muted">
                  <div className="d-flex justify-content-between">
                    <span>Created:</span>
                    <span>{formatDate(tutorial.createdAt)}</span>
                  </div>
                  {tutorial.modifiedAt && tutorial.modifiedAt !== tutorial.createdAt && (
                    <div className="d-flex justify-content-between">
                      <span>Modified:</span>
                      <span>{formatDate(tutorial.modifiedAt)}</span>
                    </div>
                  )}
                  {tutorial.creator && (
                    <div className="d-flex justify-content-between">
                      <span>By:</span>
                      <span>{tutorial.creator.emailAddress}</span>
                    </div>
                  )}
                </div>

                <div className="d-flex gap-2 mt-auto">
                  <button
                    className="btn btn-sm btn-outline-primary flex-fill"
                    onClick={() => handleView(tutorial)}
                    disabled={loading}
                  >
                    View
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => handleOpenEditModal(tutorial)}
                    disabled={loading}
                  >
                    Edit
                  </button>
                  <button
                    className={`btn btn-sm ${
                      tutorial.isPublished ? 'btn-outline-warning' : 'btn-outline-success'
                    }`}
                    onClick={() => handleTogglePublish(tutorial)}
                    disabled={loading}
                  >
                    {tutorial.isPublished ? 'Unpublish' : 'Publish'}
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDelete(tutorial)}
                    disabled={loading}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modalMode === 'create' ? 'Create Tutorial' : 'Edit Tutorial'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                  disabled={loading}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="title" className="form-label fw-semibold">
                      Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="content" className="form-label fw-semibold">
                      Content / Instructions
                    </label>
                    <textarea
                      className="form-control"
                      id="content"
                      name="content"
                      rows="5"
                      value={formData.content}
                      onChange={handleInputChange}
                      disabled={loading}
                      placeholder="Enter tutorial instructions or description..."
                    ></textarea>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="file" className="form-label fw-semibold">
                      Video / PDF File {modalMode === 'edit' && '(optional - leave empty to keep existing)'}
                    </label>
                    <input
                      type="file"
                      className="form-control"
                      id="file"
                      accept="video/mp4,video/mpeg,video/quicktime,video/x-msvideo,video/x-ms-wmv,video/webm,application/pdf"
                      onChange={handleFileChange}
                      disabled={loading}
                    />
                    <div className="form-text">
                      Allowed: MP4, MPEG, MOV, AVI, WMV, WEBM, PDF (Max 500MB)
                    </div>
                    {formData.file && (
                      <div className="mt-2">
                        <small className="text-success">
                          <i className="bi bi-check-circle me-1"></i>
                          Selected: {formData.file.name} ({(formData.file.size / (1024 * 1024)).toFixed(2)} MB)
                        </small>
                      </div>
                    )}
                    {modalMode === 'edit' && selectedTutorial?.azurebloblink && (
                      <div className="mt-2">
                        <small className="text-muted">
                          <i className="bi bi-info-circle me-1"></i>
                          Current file: {selectedTutorial.azurebloblink}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseModal}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      <>{modalMode === 'create' ? 'Create' : 'Update'} Tutorial</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewTutorial && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title">{viewTutorial.title}</h5>
                  <span
                    className={`badge ${
                      viewTutorial.isPublished
                        ? 'bg-success-subtle text-success'
                        : 'bg-warning-subtle text-warning'
                    } rounded-pill mt-2`}
                  >
                    {viewTutorial.isPublished ? 'Published' : 'Draft'}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseViewModal}
                ></button>
              </div>
              <div className="modal-body">
                {viewTutorial.content && (
                  <div className="mb-4">
                    <h6 className="fw-semibold mb-2">Instructions:</h6>
                    <p className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>
                      {viewTutorial.content}
                    </p>
                  </div>
                )}

                {viewTutorial.fileUrl && (
                  <div className="mb-4">
                    <h6 className="fw-semibold mb-3">Media File:</h6>
                    {viewTutorial.azurebloblink?.toLowerCase().endsWith('.pdf') ? (
                      <div className="text-center">
                        <a
                          href={viewTutorial.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary"
                        >
                          <i className="bi bi-file-pdf me-2"></i>
                          Open PDF
                        </a>
                      </div>
                    ) : (
                      <video
                        controls
                        className="w-100 rounded"
                        style={{ maxHeight: '500px', backgroundColor: '#000' }}
                      >
                        <source src={viewTutorial.fileUrl} />
                        Your browser does not support the video tag.
                      </video>
                    )}
                  </div>
                )}

                <div className="border-top pt-3 mt-4">
                  <div className="row text-muted small">
                    <div className="col-md-6 mb-2">
                      <strong>Created:</strong> {formatDate(viewTutorial.createdAt)}
                    </div>
                    {viewTutorial.modifiedAt && viewTutorial.modifiedAt !== viewTutorial.createdAt && (
                      <div className="col-md-6 mb-2">
                        <strong>Modified:</strong> {formatDate(viewTutorial.modifiedAt)}
                      </div>
                    )}
                    {viewTutorial.creator && (
                      <div className="col-md-6 mb-2">
                        <strong>Created by:</strong> {viewTutorial.creator.emailAddress} ({viewTutorial.creator.user_type})
                      </div>
                    )}
                    {viewTutorial.azurebloblink && (
                      <div className="col-md-6 mb-2">
                        <strong>File:</strong> {viewTutorial.azurebloblink}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseViewModal}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => {
                    handleCloseViewModal();
                    handleOpenEditModal(viewTutorial);
                  }}
                >
                  <i className="bi bi-pencil me-2"></i>
                  Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorialManagement;

